import { type TFunction } from 'i18next'
import { match } from 'ts-pattern'
import { z } from 'zod'

import { type DocumentImage } from '~/gql/graphql'

import { exists, safeParseJson } from '../../../generals'
import {
  conditionOperationLabelI18nKeyMap,
  conditionOperations,
} from '../../conditionOperation'
import {
  type FigureInfo,
  type FigureInfoGroupFilter,
  type FigureInfoKey,
  type FigureInfoOverlapGroupFilter,
} from '../../figureInfo'
import { SurveyGroupAnswerOptionOp } from '../../modelOperators'
import { formatNineSegment } from '../../segmentHelpers'
import { type NineNumber } from '../../segments'
import { type FilterText } from '../../types'

import { getMetaValue } from './getMetaValue'

const groupsFilterSchema = z.array(
  z.object({
    brands: z.array(
      z.object({
        name: z.string(),
        segments: z.array(z.string()),
      }),
    ),
    name: z.string(),
  }),
) satisfies z.ZodSchema<FigureInfoGroupFilter[]>

const overlapGroupFiltersSchema = z.array(
  z.object({
    brand1: z.string(),
    brand2: z.string(),
    combinations: z.array(
      z.object({
        brand1: z.string(),
        brand2: z.string(),
        combination: z.string(),
      }),
    ),
    name: z.string(),
  }),
) satisfies z.ZodSchema<FigureInfoOverlapGroupFilter[]>

const filterTextGroupsSchema = z.array(
  z.array(
    z.object({
      name: z.string(),
      subName: z.string().optional(),
      values: z.array(z.string()),
    }),
  ),
) satisfies z.ZodSchema<FilterText[][]>

const segmentNumberSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
]) satisfies z.ZodSchema<NineNumber>

const surveyFilterV1Schema = z.object({
  questionAnswerConditions: z
    .array(
      z.object({
        question: z.object({ question_text: z.string() }),
        conditionOperation: z.enum(conditionOperations),
        answers: z.union([
          z.array(z.object({ option: z.number(), text: z.string() })),
          z.array(z.string()),
        ]),
      }),
    )
    .optional()
    .default([]),
  segmentConditions: z
    .array(
      z.object({
        brandGroup: z.object({ name: z.string() }),
        brand: z.object({ name: z.string() }),
        segmentNumbers: z.array(segmentNumberSchema),
      }),
    )
    .optional()
    .default([]),
})
// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
const getSurveyFilterV1ToV2Schema = (t: TFunction) =>
  surveyFilterV1Schema.transform(
    ({ questionAnswerConditions, segmentConditions }): FilterText[][] => {
      const questionFilterTexts = questionAnswerConditions.map<FilterText>(
        ({ answers, conditionOperation, question }) => ({
          name: question.question_text,
          subName: t(conditionOperationLabelI18nKeyMap[conditionOperation]),
          values: (
            answers as
              | {
                  option: number
                  text: string
                }[]
              | string[]
          ).map((answer) =>
            typeof answer === 'string'
              ? answer
              : `${answer.option} - ${answer.text}`,
          ),
        }),
      )

      const brandSegmentsFilterTexts = segmentConditions.map<FilterText>(
        ({ brand, brandGroup, segmentNumbers }) => ({
          name: brandGroup.name,
          subName: brand.name,
          values: segmentNumbers.map((segmentNumber) =>
            formatNineSegment(segmentNumber, '%t1', t),
          ),
        }),
      )

      return brandSegmentsFilterTexts.length === 0
        ? [questionFilterTexts]
        : brandSegmentsFilterTexts.map((brandSegmentsFilterText) => [
            ...questionFilterTexts,
            brandSegmentsFilterText,
          ])
    },
  )

const surveyGroupFilterV1Schema = z.object({
  surveyGroupQuestionAnswerConditions: z
    .array(
      z.object({
        surveyGroupQuestion: z.object({ name: z.string() }),
        conditionOperation: z.enum(conditionOperations),
        surveyGroupAnswers: z.array(
          z.object({ option: z.string(), text: z.string() }),
        ),
      }),
    )
    .optional()
    .default([]),
  surveyGroupSegmentConditions: z
    .array(
      z.object({
        surveyGroupBrand: z.object({ name: z.string() }),
        segmentNumbers: z.array(segmentNumberSchema),
      }),
    )
    .optional()
    .default([]),
})
// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
const getSurveyGroupFilterV1ToV2Schema = (t: TFunction) =>
  surveyGroupFilterV1Schema.transform(
    ({
      surveyGroupQuestionAnswerConditions,
      surveyGroupSegmentConditions,
    }): FilterText[][] => {
      const questionFilterTexts =
        surveyGroupQuestionAnswerConditions.map<FilterText>(
          ({
            conditionOperation,
            surveyGroupAnswers,
            surveyGroupQuestion,
          }) => ({
            name: surveyGroupQuestion.name,
            subName: t(conditionOperationLabelI18nKeyMap[conditionOperation]),
            values: surveyGroupAnswers.map(SurveyGroupAnswerOptionOp.toText),
          }),
        )

      const brandSegmentsFilterTexts =
        surveyGroupSegmentConditions.map<FilterText>(
          ({ segmentNumbers, surveyGroupBrand }) => ({
            name: surveyGroupBrand.name,
            values: segmentNumbers.map((segmentNumber) =>
              formatNineSegment(segmentNumber, '%t1', t),
            ),
          }),
        )

      return brandSegmentsFilterTexts.length === 0
        ? [questionFilterTexts]
        : brandSegmentsFilterTexts.map((brandSegmentsFilterText) => [
            ...questionFilterTexts,
            brandSegmentsFilterText,
          ])
    },
  )

/**
 * DocumentImage から、指定した FigureInfoKey の値を取得します。
 *
 * @param documentImage - DocumentImage。
 * @param figureInfoKey - FigureInfoKey。
 * @param t - Translation function。
 * @returns 指定した FigureInfoKey の値。
 *
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export function getFigureInfoValue(
  documentImage: App.PickGraphQL<DocumentImage, 'allMetadata' | 'pathKey'>,
  figureInfoKey: FigureInfoKey,
  t: TFunction,
): ValueOf<FigureInfo> | undefined {
  const value = getMetaValue(documentImage, figureInfoKey)
  if (!exists(value)) {
    return value
  }

  const jsonValue = safeParseJson(value)
  // creator は JSON.parse した文字列をそのまま返す
  if (figureInfoKey === 'creator') {
    return typeof jsonValue === 'string' ? jsonValue : value
  }
  if (!exists(jsonValue)) {
    return undefined
  }

  const schema = match(figureInfoKey)
    .with(
      'screenLabel',
      'figureType',
      'survey',
      'brandGroup',
      'brand',
      'surveyGroup',
      'tableChartType',
      'dataDisplayType',
      'segmentDisplayType',
      'segmentRoute',
      'npiDriverQuestion',
      'importanceQuestion',
      'liftSaQuestion',
      'isValueAxisMaxStandardized',
      'overlapBrand1',
      'overlapBrand2',
      'resultViewerType',
      () => z.string(),
    )
    .with('columnCustomerAttributes', 'questions', 'customerAttributes', () =>
      z.array(z.string()),
    )
    .with('groupFilters', () => groupsFilterSchema)
    .with('overlapGroupFilters', () => overlapGroupFiltersSchema)
    .with('surveyFilter', () =>
      filterTextGroupsSchema.or(getSurveyFilterV1ToV2Schema(t)),
    )
    .with('surveyGroupFilter', () =>
      filterTextGroupsSchema.or(getSurveyGroupFilterV1ToV2Schema(t)),
    )
    .exhaustive()
  const parsed = schema.safeParse(jsonValue)

  return parsed.success ? parsed.data : undefined
}
